/**
 * Phone-number validation helpers shared across login/signup/forgot-password flows.
 *
 * The countryRules array (utils/const.js) carries a `phone_regex` per country,
 * stored as a string. This module compiles those into RegExp objects on demand
 * and returns user-friendly validation results.
 */

/**
 * Strip everything except digits from the local part of a phone number.
 * (Country code dialing prefix is handled separately.)
 */
export const cleanLocalNumber = (raw) => String(raw || "").replace(/\D/g, "");

/**
 * Build the full E.164-style number used by the backend (no leading "+",
 * just digits including country code).
 */
export const buildFullNumber = (countryCode, localNumber) => {
  const cc = normalizeDialCode(countryCode);
  const ln = cleanLocalNumber(localNumber);
  return cc + ln;
};

const normalizeDialCode = (countryCode) => {
  const raw = String(countryCode || "");
  if (!raw) return "";

  // Some entries have multiple alternatives, e.g. "+1-809, +1-829, +1-849".
  // Use the first selectable code for deterministic number construction.
  const first = raw.split(",")[0].trim();
  return first.replace(/\D/g, "");
};

/**
 * Validate the entered local number against the country's phone_regex.
 *
 * Returns: { valid: boolean, message?: string, fullNumber?: string }
 */
export const validateWhatsappNumber = (countryRules, countryCode, localNumber) => {
  const ln = cleanLocalNumber(localNumber);

  if (!countryCode) {
    return { valid: false, message: "Please select a country code." };
  }

  if (!ln) {
    return { valid: false, message: "Please enter your WhatsApp number." };
  }

  // Match by phone_code (country.phone_code starts with "+")
  const country = (countryRules || []).find(
    (c) => c.phone_code === countryCode
  );

  // Reasonable length sanity check: between 6 and 15 digits worldwide.
  if (ln.length < 6 || ln.length > 15) {
    return {
      valid: false,
      message: "WhatsApp number length looks invalid. Please double-check.",
    };
  }

  if (country && country.phone_regex) {
    const expected = extractLocalDigitRange(country);
    if (expected) {
      if (ln.length < expected.min || ln.length > expected.max) {
        return {
          valid: false,
          message: `Invalid format for ${country.name}. Expected ${describeExpectedFormat(
            country
          )}.`,
        };
      }
    } else {
      // Fallback: if regex shape is not parseable, verify against full number.
      let regex;
      try {
        regex = new RegExp(country.phone_regex);
      } catch {
        regex = null;
      }
      if (regex) {
        const fullWithPlus = `${country.phone_code}${ln}`;
        if (!regex.test(fullWithPlus)) {
          return {
            valid: false,
            message: `Invalid format for ${country.name}. Expected ${describeExpectedFormat(
              country
            )}.`,
          };
        }
      }
    }
  }

  return {
    valid: true,
    fullNumber: buildFullNumber(countryCode, ln),
  };
};

/**
 * Try to derive an example/expected format from the regex string.
 * Falls back to a generic hint if we can't parse it.
 */
const describeExpectedFormat = (country) => {
  if (!country?.phone_regex) return "a valid WhatsApp number";
  const localRange = extractLocalDigitRange(country);
  if (localRange) {
    const min = localRange.min;
    const max = localRange.max;
    const rangeText = max && max !== min ? `${min}–${max}` : min;
    return `${rangeText} digits after country code ${country.phone_code}`;
  }
  return `a valid number after country code ${country.phone_code}`;
};

const extractLocalDigitRange = (country) => {
  if (!country?.phone_regex || !country?.phone_code) return null;

  const ccEscaped = country.phone_code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Matches patterns like ^\+856\d{10}$ or ^\+56\d{8,9}$
  const match = country.phone_regex.match(
    new RegExp(`^\\^${ccEscaped}\\\\d\\{(\\d+)(?:,(\\d+))?\\}\\$$`)
  );

  if (!match) return null;

  const min = parseInt(match[1], 10);
  const max = parseInt(match[2] || match[1], 10);
  if (Number.isNaN(min) || Number.isNaN(max)) return null;

  return { min, max };
};
